import React, { forwardRef, useImperativeHandle, useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import moment from 'moment'
import { useHistory, useLocation } from 'react-router-dom'
import Skeleton from 'react-loading-skeleton'

import Api from '@/_helpers/Api'
import * as Constants from '@/_config/app'
import { trans } from '@/_providers/Translation'
import { useAuthDataContext } from '@/_providers/Auth'
import { useSocketContext } from '@/_providers/Socket'
import Pagination from '@/components/Pagination'
import NoDataFound from '@/components/partials/NoDataFound'
import Question from '@/components/modals/messages/Question'

import AddOrEdit from './AddOrEdit'
import SendEmail from './SendEmail'
import Options from './Options'
import {
    ENDPOINTS,
    CONTRACTS_QUERY_KEY,
    CONTRACT_SIGNED_EVENT,
    isSent,
    isSigned,
    isTerminated,
} from './config'

import '@/assets/scss/groups/contracts.scss'

// Списък с договори. Модулът е един и същ за всички страници, които го
// показват - разликата е само в `typeId` (contract_type), който се изпраща с
// всяка заявка, за да знае бекендът кой точно модул обслужва.
function Index(props, ref) {

    const contractType = props.typeId

    const auth = useAuthDataContext()
    const listener = useSocketContext()
    const partnerId = auth.partnerId()
    const queryClient = useQueryClient()

    const history = useHistory()
    const location = useLocation()

    const searchParams = new URLSearchParams(location.search)

    const modalAddOrEditRef = useRef(null)
    const modalSendRef = useRef(null)
    const modalDeleteRef = useRef(null)
    const modalTerminateRef = useRef(null)

    const searchTimeoutRef = useRef(null)

    const page = parseInt(searchParams.get('page')) || 1
    const name = searchParams.get('name') || ''
    const contractorId = props.contractorId || null

    // Стойността на търсачката се държи отделно, за да пише плавно - в адреса
    // отива чак след кратка пауза.
    const [searchValue, setSearchValue] = useState(name)

    useImperativeHandle(ref, () => ({
        add: () => {
            handleAdd()
        }
    }))

    useEffect(() => {
        setSearchValue(name)
    }, [name])

    // read
    const { data: queryData, isLoading } = useQuery({
        queryKey: [CONTRACTS_QUERY_KEY, contractType, contractorId, page, name],
        queryFn: async () => {
            const res = await Api.get(ENDPOINTS.list, {
                params: {
                    contract_type: contractType,
                    contractor_id: contractorId,
                    page: page,
                    name: name,
                }
            })

            return res.data
        },
        // Без keepPreviousData: непозната страница няма какво да покаже, затова
        // isLoading става true и се вижда скелетонът. Вече кешираните страници
        // (и опресняването по сокет) се сменят на тихо.
    })

    const rows = queryData?.data || []
    const pages = queryData?.pages || 0

    // Презарежда на тихо - isLoading остава false, затова таблицата не мига.
    const refresh = () => {
        queryClient.invalidateQueries({ queryKey: [CONTRACTS_QUERY_KEY] })
    }

    // Клиентът подписа договора - обновяваме списъка, без да го връщаме в
    // състояние "зарежда", за да не подскача екранът под ръцете на оператора.
    useEffect(() => {
        if (!listener || !partnerId) {
            return
        }

        const channel = listener.private(`partners.${partnerId}.${contractType}`)

        const handleContractSigned = () => {
            queryClient.invalidateQueries({ queryKey: [CONTRACTS_QUERY_KEY] })
        }

        channel.listen(CONTRACT_SIGNED_EVENT, handleContractSigned)

        return () => {
            channel.stopListening(CONTRACT_SIGNED_EVENT, handleContractSigned)
        }
    }, [listener, partnerId, contractType, queryClient])

    const findContract = id => rows.find(c => c.id == id)

    // create
    const handleAdd = () => {
        modalAddOrEditRef.current.add()
    }

    // Новосъздаденият договор трябва да се появи в таблицата.
    const handleCreate = () => {
        refresh()
    }

    // update
    const handleEdit = id => {
        modalAddOrEditRef.current.edit(id)
    }

    const handleUpdate = () => {
        refresh()
    }

    // preview
    const handleView = id => {
        window.open(
            Constants.App.api + ENDPOINTS.stream
            + '?id=' + id
            + '&contract_type=' + contractType
            + '&token=' + auth.token(),
            '_blank'
        )
    }

    // send
    const handleSend = id => {
        modalSendRef.current.open(id, findContract(id))
    }

    const handleSent = () => {
        refresh()
    }

    // delete
    const handleDestroy = id => {
        Api.post(ENDPOINTS.delete, {
            id: id,
            contract_type: contractType,
        }).then(res => {
            if (res.data.success) {
                modalDeleteRef.current.close()

                refresh()
            }
        })
    }

    const handleDelete = id => {
        modalDeleteRef.current.open(id)

        modalDeleteRef.current.onSuccess(() => {
            handleDestroy(id)
        })
    }

    // terminate
    const handleTerminated = id => {
        Api.post(ENDPOINTS.terminate, {
            id: id,
            contract_type: contractType,
        }).then(() => {
            modalTerminateRef.current.close()

            refresh()
        })
    }

    const handleTerminate = id => {
        modalTerminateRef.current.open(id)

        modalTerminateRef.current.onSuccess(() => {
            handleTerminated(id)
        })
    }

    // filters
    const pushUrl = (nextPage, nextName) => {
        searchParams.set('page', nextPage)
        searchParams.set('name', nextName)

        history.push('?' + searchParams.toString())
    }

    const handleSearch = (key, val) => {
        clearTimeout(searchTimeoutRef.current)

        setSearchValue(val)

        searchTimeoutRef.current = setTimeout(() => {
            pushUrl(1, val)
        }, 300)
    }

    const handlePage = selected => {
        pushUrl(selected.selected + 1, name)
    }

    const renderStatus = contract => {
        if (isTerminated(contract)) {
            return <span className="contract-status terminated">{trans('statuses.terminated')}</span>
        }

        if (isSigned(contract)) {
            return <span className="contract-status signed">{trans('statuses.signed')}</span>
        }

        if (isSent(contract)) {
            return <span className="contract-status sent">{trans('statuses.sent')}</span>
        }

        return <span className="contract-status not-sent">{trans('statuses.notSent')}</span>
    }

    return (
        <>
            <AddOrEdit
                ref={modalAddOrEditRef}
                typeId={contractType}
                contractorId={props.contractorId}
                contractor={props.contractor}
                handleCreate={handleCreate}
                handleUpdate={handleUpdate}
            />

            <SendEmail
                ref={modalSendRef}
                typeId={contractType}
                handleSent={handleSent}
            />

            <Question
                ref={modalDeleteRef}
                mainMessage={trans('headings.confirmDelete')}
                secondaryMessage={trans('subheadings.confirmDelete')}
            />

            <Question
                ref={modalTerminateRef}
                mainMessage={trans('headings.confirmTerminate')}
                secondaryMessage={trans('subheadings.confirmTerminate')}
            />

            <div className="pannel">
                <div className="filter-standard">
                    <div className="col">
                        <input
                            type="search"
                            name="name"
                            onChange={e => handleSearch(e.target.name, e.target.value)}
                            value={searchValue}
                            placeholder={trans('labels.search')}
                        />
                    </div>
                </div>

                <hr />

                {isLoading
                    ?
                    <Skeleton height={45} count={10} />
                    :
                    rows.length === 0
                        ?
                        <NoDataFound />
                        :
                        <>
                            <div className="table-scroller">
                                <table>
                                    <thead>
                                        <tr>
                                            <th style={{ width: '180px' }}>
                                                {trans('tables.number')}
                                            </th>
                                            <th>
                                                {trans('tables.title')}
                                            </th>
                                            <th>
                                                {trans('labels.contractTo')}
                                            </th>
                                            <th style={{ width: '180px' }}>
                                                {trans('tables.creationDate')}
                                            </th>
                                            <th className="center" style={{ width: '150px' }}>
                                                {trans('tables.status')}
                                            </th>
                                            <th className="options">
                                                {trans('tables.options')}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map(c =>
                                            <tr key={c.id}>
                                                <td>
                                                    {c.contract_num || c.id}
                                                </td>
                                                <td>
                                                    <span className="link" onClick={() => handleView(c.id)}>
                                                        {c.title || '-'}
                                                    </span>
                                                </td>
                                                <td>
                                                    {c.contractor_name_display || '-'}
                                                </td>
                                                <td>
                                                    {c.created_at ? moment(c.created_at).format('DD.MM.YYYY / HH:mm') : '-'}
                                                </td>
                                                <td className="center">
                                                    {renderStatus(c)}
                                                </td>
                                                <td className="options">
                                                    <Options
                                                        id={c.id}
                                                        data={c}
                                                        handleView={handleView}
                                                        handleSend={handleSend}
                                                        handleTerminate={handleTerminate}
                                                        handleEdit={handleEdit}
                                                        handleDelete={handleDelete}
                                                    />
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            <Pagination
                                pages={pages}
                                page={page}
                                handlePage={handlePage}
                            />
                        </>
                }
            </div>
        </>
    )
}

export default forwardRef(Index)
